import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';

import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { firstValueFrom, forkJoin } from 'rxjs';

import { SETTINGS, Settings } from '../../../app.settings';

import { isEmpty } from '../../../common/functions';
import { successMsg } from '../../../common/messages';
import { notifyAndTrackError } from '../../../common/error';

import { AuditInfoComponent, CanComponentDeactivate, ProcessingComponent, UnsavedChangesDialogComponent } from '../../../components';

import { AppInsightsService } from '../../../services/app-insights.service';
import { IProject, ProjectAdminService } from '../../../services/insecta';
import { MSGraphService } from '../../../services/msgraph.service';

@Component({
  selector: 'app-Project-admin',
  standalone: true,
  imports: [
    BreadcrumbModule,
    ButtonModule,
    CardModule,
    CheckboxModule,
    CommonModule,
    DialogModule,
    FloatLabelModule,
    FormsModule,
    InputTextModule,
    RouterModule,
    TableModule,
    ToggleSwitchModule,
    AuditInfoComponent,
    ProcessingComponent,
    UnsavedChangesDialogComponent
  ],

  templateUrl: 'project-admin.component.html'
})
export class ProjectAdminComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  data: IProject[] = [];
  modifiedData: { [s: string]: IProject } = {};

  dialogConfirmSub: ((leave: boolean) => void) | null = null;

  dialogVars: any = {
    visible: false,
    data: <IProject>{}
  };

  pageVars: any = {
    currentUser: ``,
    isEditing: false,
    isReady: false,
    showUnsavedChangesDialog: false,
    pageId: `Admin:Projects`,
    breadcrumbItems: <MenuItem[]>[{ label: 'Global Lists', routerLink: '/admin/globallists' }, { label: 'Cost Center Management' }]
  };

  constructor(
    private apiService: ProjectAdminService,
    private appInsights: AppInsightsService,
    private graphService: MSGraphService,
    private msgService: MessageService,
    private router: Router,
    @Inject(SETTINGS) private settings: Settings
  ) {
    this.pageVars.pageId = `${settings.environment}:Admin:Projects`;
    appInsights.trackPageView(this.pageVars.pageId, `/admin/projects`);
    this.dialogVars.data = <IProject>{};
  }

  canDeactivate(): Promise<boolean> {
    if (!this.pageVars.isEditing) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      this.pageVars.showUnsavedChangesDialog = true;

      const confirmSub = (decision: boolean) => {
        this.dialogConfirmSub = null;
        resolve(decision);
      };

      this.dialogConfirmSub = confirmSub;
    });
  }

  onDialogConfirm(leave: boolean) {
    this.pageVars.showUnsavedChangesDialog = false;
    if (this.dialogConfirmSub) {
      this.dialogConfirmSub(leave);
    }
  }

  ngOnDestroy() {
    this.data = [];
    this.pageVars = {
      isReady: false
    };
  }

  ngOnInit() {
    this.appInsights.trackEvent(`${this.pageVars.pageId}:Init`);

    const request = {
      profile: this.graphService.getMyProfile$(),
      Projects: this.apiService.getProjectsAll()
    };

    firstValueFrom(forkJoin(request))
      .then((response) => {
        this.data = response.Projects;
        this.pageVars.currentUser = response.profile.userPrincipalName;
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, {
          component: this.pageVars.pageId,
          action: 'Init:Error'
        });
      })
      .finally(() => {
        this.pageVars.isReady = true;
        this.pageVars.isEditing = false;
      });
  }

  backClick() {
    this.router.navigate(['admin/globallists']);
  }

  createDlgCancel() {
    this.dialogVars.visible = false;
    this.dialogVars.data = <IProject>{};
  }

  createDlgSave() {
    this.dialogVars.data.action = `INS`;
    this.dialogVars.data.actionBy = this.pageVars.currentUser;
    this.dialogVars.data.actionOn = new Date(Date.now());

    this.appInsights.trackEvent(`${this.pageVars.pageId}:Grid:Insert`, { Project: this.dialogVars.data });

    firstValueFrom(this.apiService.addProject(this.dialogVars.data))
      .then((result) => {
        this.dialogVars.data.id = result;
        this.data = [this.dialogVars.data, ...this.data];

        successMsg(this.msgService, 'Cost Center has been created');

        this.dialogVars.visible = false;
        this.dialogVars.data = <IProject>{};
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, {
          component: this.pageVars.pageId,
          action: 'Grid:Insert:Error'
        });
      });
  }

  dialogIsValid(): boolean {
    const nameValue = this.dialogVars?.data?.name?.trim() ?? '';
    if (isEmpty(nameValue)) return false;
    if (nameValue === '') return false;

    return true;
  }

  gridNewRow() {
    this.dialogVars.data = <IProject>{
      isActive: true,
      actionBy: this.pageVars.currentUser
    };

    this.dialogVars.visible = true;
  }

  gridRowEditCancel(c: IProject, idx: number) {
    this.data[idx] = this.modifiedData[c.id as string];
    delete this.modifiedData[c.id as string];
    this.pageVars.isEditing = false;
  }

  gridRowEditInit(c: IProject) {
    this.pageVars.isEditing = true;
    this.modifiedData[c.id as string] = { ...c };
    c.action = 'UPD';
    c.actionBy = this.pageVars.currentUser;
    c.actionOn = new Date(Date.now());
  }

  gridRowEditSave(c: IProject, idx: number) {
    this.pageVars.isEditing = true;
    this.pageVars.isReady = false;

    this.appInsights.trackEvent(`${this.pageVars.pageId}:Grid:Save`, { Project: c });

    firstValueFrom(this.apiService.updateProject(c))
      .then(() => {
        successMsg(this.msgService, 'Cost Center is updated');
      })
      .catch((err) => {
        this.data[idx] = this.modifiedData[c.id as string];
        notifyAndTrackError(this.msgService, this.appInsights, err, {
          component: this.pageVars.pageId,
          action: 'Grid:Save:Error'
        });
      })
      .finally(() => {
        delete this.modifiedData[c.id as string];
        this.pageVars.isReady = true;
        this.pageVars.isEditing = false;
      });
  }

  toggleStatus(item: IProject) {
    firstValueFrom(this.apiService.updateProject(item))
      .then(() => {
        const idx = this.data.findIndex((x) => x.id == item.id);
        this.data[idx].isActive = item.isActive;
        this.data[idx].action = 'UPD';
        this.data[idx].actionBy = this.pageVars.currentUser;
        this.data[idx].actionOn = new Date();
        successMsg(this.msgService, 'Cost Center is updated');
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, {
          component: this.pageVars.pageId,
          action: 'ToggleStatus:Error'
        });
      });
  }
}
